import { Autocomplete, TextField, Box, Typography } from '@mui/material';

import { useEffect, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';

import { useGetCountryCodes } from '../../lib/user/useGetCountryCodes';
import { CountryCode } from '../../types/CountryCode';

interface PhoneNumberInputProps {
  name?: string;
  value?: string;
  placeholder?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: boolean;
  helperText?: string | false;
  onCountryChange?: (country: CountryCode) => void;
}

const PhoneNumberInput = ({
  name = 'phone',
  value = '',
  placeholder,
  onChange,
  error,
  helperText,
  onCountryChange,
}: PhoneNumberInputProps) => {
  const { data } = useGetCountryCodes();
  const [selectedCountry, setSelectedCountry] = useState<CountryCode | undefined>(undefined);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (data && data.length > 0 && !selectedCountry) {
      const defaultCountry = data.find((country) => country.iso2 === 'US') || data[0];
      setSelectedCountry(defaultCountry);
      setInputValue(defaultCountry.dialCode);
      onCountryChange?.(defaultCountry);
    }
  }, [data, selectedCountry, onCountryChange]);

  const handleCountryChange = (newValue: CountryCode) => {
    setSelectedCountry(newValue);
    setInputValue(newValue.dialCode);
    onCountryChange?.(newValue);
  };

  const phoneValue = value?.startsWith(selectedCountry?.dialCode || '') ? value.slice(selectedCountry?.dialCode.length || 0) : value;

  if (!selectedCountry) {
    return null;
  }

  return (
    <Box sx={{ flex: 1, display: 'flex' }}>
      <Autocomplete<CountryCode, false, true, false>
        options={data || []}
        value={selectedCountry}
        inputValue={inputValue}
        onChange={(_, newValue) => handleCountryChange(newValue)}
        getOptionLabel={(option) => option.dialCode}
        isOptionEqualToValue={(option, value) => option.iso2 === value.iso2}
        filterOptions={(options, { inputValue }) => {
          const searchTerm = inputValue.toLowerCase();
          return options.filter(
            (option) =>
              option.name.toLowerCase().includes(searchTerm) ||
              option.dialCode.includes(searchTerm) ||
              option.iso2.toLowerCase().includes(searchTerm),
          );
        }}
        slotProps={{
          popper: {
            sx: {
              width: 'auto !important',
              minWidth: '200px',
            },
          },
        }}
        disableClearable
        renderOption={(props, option) => (
          <Box component="li" {...props} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <ReactCountryFlag countryCode={option.iso2} style={{ width: '2em', height: '2em' }} svg />
            <Typography variant="caption" fontWeight={700}>
              {option.name}
            </Typography>
            <Typography variant="caption">-</Typography>
            <Typography variant="caption">{option.dialCode}</Typography>
          </Box>
        )}
        sx={{
          minWidth: '130px',
          '& .MuiOutlinedInput-root': {
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            inputProps={{
              ...params.inputProps,
              style: { fontWeight: 500 },
              fontSize: 12,
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <ReactCountryFlag countryCode={selectedCountry.iso2} style={{ width: '1.2em', height: '1.2em', margin: '0 8px' }} svg />
              ),
            }}
            error={error}
            helperText={helperText}
          />
        )}
      />
      <TextField
        name={name}
        value={phoneValue}
        placeholder={placeholder}
        onChange={onChange}
        sx={{
          flex: 1,
          '& .MuiOutlinedInput-root': {
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            borderLeft: 'none',
          },
        }}
      />
    </Box>
  );
};

export default PhoneNumberInput;
